import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Header from "@/components/layout/header";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  RefreshCw,
  Trash2,
  RotateCcw,
  Download,
  AlertTriangle,
  CheckCircle,
  Clock,
  Package,
  AlertCircle,
  Eye,
  Filter,
  X
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/api";
import { ViewGuard } from "@/components/auth/PermissionGuards";
import { usePermissions } from "@/contexts/PermissionContext";
import { MonitoringLayout } from "@/pages/monitoring/layout";

// Types
interface QueueStatus {
  name: string;
  messageCount: number;
  visibleMessageCount: number;
  oldestMessageTime?: string;
  isPoisonQueue: boolean;
  status: 'healthy' | 'warning' | 'error';
  lastUpdated: string;
}

interface QueueStatusResponse {
  timestamp: string;
  queues: QueueStatus[];
  summary: {
    totalQueues: number;
    totalMessages: number;
    poisonMessages: number;
    healthyQueues: number;
    overallStatus: string;
  };
}

interface QueueMessage {
  id: string;
  body: any;
  batchId?: string;
  entityName?: string;
  size: number;
}

interface QueueMessagesResponse {
  timestamp: string;
  queueName: string;
  totalMessages: number;
  messages: QueueMessage[];
  batchSummary: Array<{
    batchId: string;
    messageCount: number;
    totalSize: number;
  }>;
}

interface BatchInfo {
  batchId: string;
  messageCount: number;
  firstSeen: string;
  lastSeen: string;
  queues: string[];
  status: 'active' | 'completed' | 'failed';
}

interface BatchInfoResponse {
  timestamp: string;
  totalBatches: number;
  batches: BatchInfo[];
}

// API functions
const fetchQueueStatus = async (): Promise<QueueStatusResponse> => {
  const response = await apiRequest('GET', '/api/queue-management?action=status');
  if (!response.ok) {
    throw new Error('Failed to fetch queue status');
  }
  return response.json();
};

const fetchQueueMessages = async (queue: string, batchId?: string, entityName?: string, limit: number = 50): Promise<QueueMessagesResponse> => {
  const params = new URLSearchParams({
    action: 'messages',
    queue,
    limit: limit.toString()
  });

  if (batchId) params.append('batchId', batchId);
  if (entityName) params.append('entityName', entityName);

  const response = await apiRequest('GET', `/api/queue-management?${params}`);
  if (!response.ok) {
    throw new Error('Failed to fetch queue messages');
  }
  return response.json();
};

const fetchBatchInfo = async (): Promise<BatchInfoResponse> => {
  const response = await apiRequest('GET', '/api/queue-management?action=batches');
  if (!response.ok) {
    throw new Error('Failed to fetch batch info');
  }
  return response.json();
};

const purgeMessages = async (queue: string, batchId?: string, dryRun: boolean = false, confirmAll?: boolean): Promise<any> => {
  const params = new URLSearchParams({
    action: 'purge',
    queue,
    dryRun: dryRun.toString()
  });

  if (batchId) {
    params.append('batchId', batchId);
  } else if (confirmAll) {
    params.append('confirmAll', 'true');
  }

  const response = await apiRequest('DELETE', `/api/queue-management?${params}`);

  if (!response.ok) {
    throw new Error('Failed to purge messages');
  }
  return response.json();
};

const reprocessMessages = async (fromQueue: string, toQueue: string, batchId?: string, limit: number = 10): Promise<any> => {
  const params = new URLSearchParams({
    action: 'reprocess',
    from: fromQueue,
    to: toQueue,
    limit: limit.toString()
  });

  if (batchId) params.append('batchId', batchId);

  const response = await apiRequest('POST', `/api/queue-management?${params}`);

  if (!response.ok) {
    throw new Error('Failed to reprocess messages');
  }
  return response.json();
};

// Helper functions
const getStatusIcon = (status: string) => {
  switch (status) {
    case 'healthy':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'warning':
      return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
    case 'error':
      return <AlertCircle className="h-4 w-4 text-red-500" />;
    default:
      return <Clock className="h-4 w-4 text-gray-500" />;
  }
};

const getStatusColor = (status: string, isPoisonQueue: boolean) => {
  if (isPoisonQueue && status === 'error') return 'bg-red-100 text-red-800';
  if (status === 'warning') return 'bg-yellow-100 text-yellow-800';
  if (status === 'healthy') return 'bg-green-100 text-green-800';
  return 'bg-gray-100 text-gray-800';
};

const formatTimeAgo = (timestamp?: string) => {
  if (!timestamp) return 'Unknown';
  const diff = Date.now() - new Date(timestamp).getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'Just now';
};

export default function QueueManagement() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canExecute = hasPermission('scheduler', 'execute');
  const noExecuteMessage = "You don't have permission to perform queue actions";
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState('pl-mapping-tasks');
  const [messageFilters, setMessageFilters] = useState({
    batchId: '',
    entityName: '',
    limit: 32  // Azure Queue Storage maximum
  });
  const [selectedMessage, setSelectedMessage] = useState<QueueMessage | null>(null);
  const [showMessageDialog, setShowMessageDialog] = useState(false);

  // Auto-refresh functionality
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ['queueStatus'] });
      queryClient.invalidateQueries({ queryKey: ['queueMessages'] });
      queryClient.invalidateQueries({ queryKey: ['batchInfo'] });
    }, 10000); // Refresh every 10 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, queryClient]);

  // Queries
  const { data: queueStatus, isLoading: statusLoading, isError: statusError, refetch: refetchStatus } = useQuery({
    queryKey: ['queueStatus'],
    queryFn: fetchQueueStatus,
    refetchInterval: autoRefresh ? 10000 : false
  });

  const { data: queueMessages, isLoading: messagesLoading, refetch: refetchMessages } = useQuery({
    queryKey: ['queueMessages', selectedQueue, messageFilters],
    queryFn: () => fetchQueueMessages(
      selectedQueue,
      messageFilters.batchId || undefined,
      messageFilters.entityName || undefined,
      messageFilters.limit
    ),
    enabled: !!selectedQueue
  });

  const { data: batchInfo, isLoading: batchLoading } = useQuery({
    queryKey: ['batchInfo'],
    queryFn: fetchBatchInfo
  });

  // Mutations
  const purgeMutation = useMutation({
    mutationFn: ({ queue, batchId, dryRun, confirmAll }: { queue: string; batchId?: string; dryRun?: boolean; confirmAll?: boolean }) =>
      purgeMessages(queue, batchId, dryRun, confirmAll),
    onSuccess: (data) => {
      toast({
        title: data.action === 'purge (dry run)' ? 'Dry Run Complete' : 'Messages Purged',
        description: data.action === 'purge (dry run)'
          ? `Would delete ${data.wouldDelete} messages`
          : `Successfully processed ${data.processedCount} messages`,
      });
      queryClient.invalidateQueries({ queryKey: ['queueStatus'] });
      queryClient.invalidateQueries({ queryKey: ['queueMessages'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Purge Failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  });

  const reprocessMutation = useMutation({
    mutationFn: ({ fromQueue, toQueue, batchId, limit }: { fromQueue: string; toQueue: string; batchId?: string; limit?: number }) =>
      reprocessMessages(fromQueue, toQueue, batchId, limit),
    onSuccess: (data) => {
      toast({
        title: 'Messages Reprocessed',
        description: `Successfully reprocessed ${data.reprocessedCount} messages from ${data.fromQueue} to ${data.toQueue}`,
      });
      queryClient.invalidateQueries({ queryKey: ['queueStatus'] });
      queryClient.invalidateQueries({ queryKey: ['queueMessages'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Reprocess Failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  });

  const handleRefresh = () => {
    refetchStatus();
    refetchMessages();
    queryClient.invalidateQueries({ queryKey: ['batchInfo'] });
  };

  const handlePurgeQueue = (queue: string, batchId?: string, confirmAll?: boolean) => {
    if (!canExecute) {
      toast({
        title: "Access denied",
        description: noExecuteMessage,
        variant: "destructive",
      });
      return;
    }
    purgeMutation.mutate({ queue, batchId, confirmAll });
  };

  const handleDryRunPurge = (queue: string, batchId?: string) => {
    if (!canExecute) {
      toast({
        title: "Access denied",
        description: noExecuteMessage,
        variant: "destructive",
      });
      return;
    }
    purgeMutation.mutate({ queue, batchId, dryRun: true });
  };

  const handleReprocess = (fromQueue: string, toQueue: string, batchId?: string) => {
    if (!canExecute) {
      toast({
        title: "Access denied",
        description: noExecuteMessage,
        variant: "destructive",
      });
      return;
    }
    reprocessMutation.mutate({ fromQueue, toQueue, batchId });
  };

  const handleExport = async (queue: string, format: 'json' | 'csv' = 'json') => {
    try {
      const response = await apiRequest('GET', `/api/queue-management?action=export&queue=${queue}&format=${format}`);
      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `queue-export-${queue}-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: 'Export Complete',
        description: `Queue data exported as ${format.toUpperCase()}`
      });
    } catch (error) {
      toast({
        title: 'Export Failed',
        description: 'Failed to export queue data',
        variant: 'destructive'
      });
    }
  };

  if (statusError) {
    return (
      <ViewGuard section="scheduler" fallback={
        <div className="flex items-center justify-center h-full">
          <div className="text-center p-8">
            <div className="mb-4">
              <span className="material-icons text-6xl text-gray-400">block</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
            <p className="text-gray-600">You don't have permission to view Queue Management</p>
          </div>
        </div>
      }>
        <MonitoringLayout>
          <div className="flex flex-col h-full">
            <Header title="Queue Management" />
            <main className="flex-1 overflow-y-auto bg-[#F5F5F5] p-6">
              <div className="w-full">
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="p-6 text-center">
                    <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-red-800 mb-2">Failed to Load Queue Status</h3>
                    <p className="text-red-700 mb-4">
                      Unable to connect to the queue management API. Please check the backend service.
                    </p>
                    <Button variant="outline" onClick={handleRefresh}>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Retry
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </main>
          </div>
        </MonitoringLayout>
      </ViewGuard>
    );
  }

  return (
    <ViewGuard section="scheduler" fallback={
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-8">
          <div className="mb-4">
            <span className="material-icons text-6xl text-gray-400">block</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to view Queue Management</p>
        </div>
      </div>
    }>
    <MonitoringLayout>
    <div className="flex flex-col h-full">
      <Header
        title="Queue Management"
        onRefresh={handleRefresh}
      />
      <main className="flex-1 overflow-y-auto bg-[#F5F5F5] p-6">
        <div className="w-full space-y-6">
          {/* Control Panel */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Queue Control Center
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Label htmlFor="auto-refresh" className="text-sm">Auto-refresh</Label>
                  <input
                    id="auto-refresh"
                    type="checkbox"
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                    className="rounded"
                  />
                  <Button variant="outline" size="sm" onClick={handleRefresh}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${(statusLoading || messagesLoading) ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {statusLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {[...Array(4)].map((_, i) => (
                    <Skeleton key={i} className="h-20" />
                  ))}
                </div>
              ) : queueStatus ? (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                    <Card className="bg-blue-50 border-blue-200">
                      <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-blue-600">{queueStatus.summary.totalMessages}</div>
                        <div className="text-sm text-blue-700">Total Messages</div>
                      </CardContent>
                    </Card>
                    <Card className="bg-green-50 border-green-200">
                      <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-green-600">{queueStatus.summary.healthyQueues}</div>
                        <div className="text-sm text-green-700">Healthy Queues</div>
                      </CardContent>
                    </Card>
                    <Card className={`${queueStatus.summary.poisonMessages > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
                      <CardContent className="p-4 text-center">
                        <div className={`text-2xl font-bold ${queueStatus.summary.poisonMessages > 0 ? 'text-red-600' : 'text-gray-600'}`}>
                          {queueStatus.summary.poisonMessages}
                        </div>
                        <div className={`text-sm ${queueStatus.summary.poisonMessages > 0 ? 'text-red-700' : 'text-gray-700'}`}>
                          Poison Messages
                        </div>
                      </CardContent>
                    </Card>
                    <Card className={`${queueStatus.summary.overallStatus === 'healthy' ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
                      <CardContent className="p-4 text-center flex items-center justify-center">
                        {getStatusIcon(queueStatus.summary.overallStatus)}
                        <div className="ml-2 text-sm font-medium">
                          {queueStatus.summary.overallStatus.toUpperCase()}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Queue Status Table */}
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Queue Name</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Total Messages</TableHead>
                          <TableHead>Visible/Peeked</TableHead>
                          <TableHead>Oldest Message</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {queueStatus.queues.map((queue) => (
                          <TableRow key={queue.name}>
                            <TableCell className="font-medium">
                              {queue.name}
                              {queue.isPoisonQueue && (
                                <Badge variant="destructive" className="ml-2 text-xs">
                                  Poison
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge className={getStatusColor(queue.status, queue.isPoisonQueue)}>
                                {getStatusIcon(queue.status)}
                                <span className="ml-1">{queue.status}</span>
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col">
                                <span className="font-semibold">{queue.messageCount.toLocaleString()}</span>
                                {queue.messageCount > 1000 && (
                                  <span className="text-xs text-gray-500">
                                    ({Math.round(queue.messageCount / 1000)}K)
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col">
                                <span>{queue.visibleMessageCount}</span>
                                {queue.messageCount > queue.visibleMessageCount && (
                                  <span className="text-xs text-gray-500">
                                    of {queue.messageCount}
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-sm text-gray-600">
                              {formatTimeAgo(queue.oldestMessageTime)}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedQueue(queue.name);
                                    // Scroll to the tabs section
                                    setTimeout(() => {
                                      document.getElementById('queue-tabs')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    }, 100);
                                    toast({
                                      title: `Viewing ${queue.name}`,
                                      description: queue.messageCount > 0
                                        ? `${queue.messageCount} messages in queue`
                                        : 'Queue is empty',
                                    });
                                  }}
                                >
                                  <Eye className="h-4 w-4" />
                                  View
                                </Button>
                                {queue.messageCount > 0 && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleExport(queue.name, 'json')}
                                    >
                                      <Download className="h-4 w-4" />
                                    </Button>
                                    {queue.isPoisonQueue && (
                                      <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            disabled={!canExecute}
                                            title={!canExecute ? noExecuteMessage : undefined}
                                          >
                                            <RotateCcw className="h-4 w-4" />
                                            Reprocess
                                          </Button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent>
                                          <AlertDialogHeader>
                                            <AlertDialogTitle>Reprocess Poison Messages</AlertDialogTitle>
                                            <AlertDialogDescription>
                                              This will move up to 10 messages from {queue.name} back to the main queue for reprocessing.
                                              Are you sure you want to continue?
                                            </AlertDialogDescription>
                                          </AlertDialogHeader>
                                          <AlertDialogFooter>
                                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                                            <AlertDialogAction
                                              onClick={() => {
                                                const mainQueue = queue.name.replace('-poison', '');
                                                handleReprocess(queue.name, mainQueue);
                                              }}
                                              disabled={!canExecute}
                                              title={!canExecute ? noExecuteMessage : undefined}
                                            >
                                              Reprocess
                                            </AlertDialogAction>
                                          </AlertDialogFooter>
                                        </AlertDialogContent>
                                      </AlertDialog>
                                    )}
                                    <AlertDialog>
                                      <AlertDialogTrigger asChild>
                                        <Button
                                          size="sm"
                                          variant="destructive"
                                          disabled={!canExecute}
                                          title={!canExecute ? noExecuteMessage : undefined}
                                        >
                                          <Trash2 className="h-4 w-4" />
                                          Clear
                                        </Button>
                                      </AlertDialogTrigger>
                                      <AlertDialogContent>
                                        <AlertDialogHeader>
                                          <AlertDialogTitle>Clear Queue</AlertDialogTitle>
                                          <AlertDialogDescription>
                                            This will permanently delete ALL {queue.messageCount} messages in {queue.name}.
                                            This action cannot be undone. Are you sure?
                                          </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                                          <AlertDialogAction
                                            onClick={() => handlePurgeQueue(queue.name, undefined, true)}
                                            className="bg-red-600 hover:bg-red-700"
                                            disabled={!canExecute}
                                          >
                                            Clear Queue
                                          </AlertDialogAction>
                                        </AlertDialogFooter>
                                      </AlertDialogContent>
                                    </AlertDialog>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              ) : null}
            </CardContent>
          </Card>

          {/* Detailed Queue Analysis Tabs */}
          <Tabs id="queue-tabs" value={selectedQueue} onValueChange={setSelectedQueue}>
            <TabsList className={`grid w-full ${queueStatus?.queues ? `grid-cols-${Math.min(queueStatus.queues.length, 4)}` : 'grid-cols-2'}`} style={{ gridTemplateColumns: queueStatus?.queues ? `repeat(${queueStatus.queues.length}, minmax(0, 1fr))` : undefined }}>
              {queueStatus?.queues ? (
                queueStatus.queues.map((queue) => (
                  <TabsTrigger key={queue.name} value={queue.name} className="text-xs">
                    {queue.name.replace('pl-', '').replace('-', ' ')}
                    {queue.messageCount > 0 && (
                      <Badge variant={queue.isPoisonQueue ? "destructive" : "secondary"} className="ml-1 text-xs">
                        {queue.messageCount}
                      </Badge>
                    )}
                  </TabsTrigger>
                ))
              ) : (
                <>
                  <TabsTrigger value="pl-mapping-tasks">Mapping Tasks</TabsTrigger>
                  <TabsTrigger value="pl-processing-tasks">Processing Tasks</TabsTrigger>
                </>
              )}
            </TabsList>

            <TabsContent value={selectedQueue} className="mt-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Queue: {selectedQueue}</CardTitle>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDryRunPurge(selectedQueue)}
                        disabled={!canExecute}
                        title={!canExecute ? noExecuteMessage : undefined}
                      >
                        <AlertTriangle className="h-4 w-4 mr-2" />
                        Dry Run Purge
                      </Button>
                    </div>
                  </div>

                  {/* Message Filters */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4">
                    <div>
                      <Label htmlFor="batchId">Batch ID</Label>
                      <div className="flex">
                        <Input
                          id="batchId"
                          placeholder="Filter by batch..."
                          value={messageFilters.batchId}
                          onChange={(e) => setMessageFilters(prev => ({ ...prev, batchId: e.target.value }))}
                        />
                        {messageFilters.batchId && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setMessageFilters(prev => ({ ...prev, batchId: '' }))}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="entityName">Entity Name</Label>
                      <div className="flex">
                        <Input
                          id="entityName"
                          placeholder="Filter by entity..."
                          value={messageFilters.entityName}
                          onChange={(e) => setMessageFilters(prev => ({ ...prev, entityName: e.target.value }))}
                        />
                        {messageFilters.entityName && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setMessageFilters(prev => ({ ...prev, entityName: '' }))}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="limit">Limit</Label>
                      <Select value={messageFilters.limit.toString()} onValueChange={(value) => setMessageFilters(prev => ({ ...prev, limit: parseInt(value) }))}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select limit" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10 messages</SelectItem>
                          <SelectItem value="20">20 messages</SelectItem>
                          <SelectItem value="32">32 messages (max)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-end">
                      <Button
                        onClick={() => refetchMessages()}
                        disabled={messagesLoading}
                      >
                        <Filter className="h-4 w-4 mr-2" />
                        Apply Filters
                      </Button>
                    </div>
                  </div>
                </CardHeader>

                <CardContent>
                  {messagesLoading ? (
                    <div className="space-y-2">
                      {[...Array(5)].map((_, i) => (
                        <Skeleton key={i} className="h-12 w-full" />
                      ))}
                    </div>
                  ) : queueMessages ? (
                    <>
                      {/* Batch Summary */}
                      {queueMessages.batchSummary.length > 0 && (
                        <div className="mb-6">
                          <h4 className="text-sm font-medium mb-2">Batch Summary ({queueMessages.batchSummary.length} batches)</h4>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4">
                            {queueMessages.batchSummary.slice(0, 9).map((batch) => (
                              <Card key={batch.batchId} className="p-2">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <div className="text-xs font-mono text-gray-600 truncate">
                                      {batch.batchId}
                                    </div>
                                    <div className="text-sm">
                                      {batch.messageCount} msg, {Math.round(batch.totalSize / 1024)}KB
                                    </div>
                                  </div>
                                  <div className="flex gap-1">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => setMessageFilters(prev => ({ ...prev, batchId: batch.batchId }))}
                                    >
                                      <Eye className="h-3 w-3" />
                                    </Button>
                                    <AlertDialog>
                                      <AlertDialogTrigger asChild>
                                        <Button
                                          size="sm"
                                          variant="destructive"
                                          disabled={!canExecute}
                                          title={!canExecute ? noExecuteMessage : undefined}
                                        >
                                          <Trash2 className="h-3 w-3" />
                                        </Button>
                                      </AlertDialogTrigger>
                                      <AlertDialogContent>
                                        <AlertDialogHeader>
                                          <AlertDialogTitle>Purge Batch</AlertDialogTitle>
                                          <AlertDialogDescription>
                                            This will permanently delete {batch.messageCount} messages from batch {batch.batchId}.
                                            This action cannot be undone.
                                          </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                                          <AlertDialogAction
                                            onClick={() => handlePurgeQueue(selectedQueue, batch.batchId)}
                                            className="bg-red-600 hover:bg-red-700"
                                            disabled={!canExecute}
                                            title={!canExecute ? noExecuteMessage : undefined}
                                          >
                                            Purge Batch
                                          </AlertDialogAction>
                                        </AlertDialogFooter>
                                      </AlertDialogContent>
                                    </AlertDialog>
                                  </div>
                                </div>
                              </Card>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Messages Table */}
                      <div className="rounded-md border">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Message ID</TableHead>
                              <TableHead>Batch ID</TableHead>
                              <TableHead>Entity</TableHead>
                              <TableHead>Size</TableHead>
                              <TableHead>Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {queueMessages.messages.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                                  No messages found
                                </TableCell>
                              </TableRow>
                            ) : (
                              queueMessages.messages.map((message) => (
                                <TableRow key={message.id}>
                                  <TableCell className="font-mono text-xs">
                                    {message.id.substring(0, 8)}...
                                  </TableCell>
                                  <TableCell className="font-mono text-xs">
                                    {message.batchId ? `${message.batchId.substring(0, 12)}...` : 'N/A'}
                                  </TableCell>
                                  <TableCell>{message.entityName || 'N/A'}</TableCell>
                                  <TableCell>{Math.round(message.size / 1024)} KB</TableCell>
                                  <TableCell>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        setSelectedMessage(message);
                                        setShowMessageDialog(true);
                                      }}
                                    >
                                      <Eye className="h-4 w-4" />
                                      View
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      Select a queue to view messages
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Batch Information Tab */}
          <Card>
            <CardHeader>
              <CardTitle>Batch Overview</CardTitle>
            </CardHeader>
            <CardContent>
              {batchLoading ? (
                <div className="space-y-2">
                  {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : batchInfo ? (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Batch ID</TableHead>
                        <TableHead>Messages</TableHead>
                        <TableHead>Queues</TableHead>
                        <TableHead>Duration</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {batchInfo.batches.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                            No active batches found
                          </TableCell>
                        </TableRow>
                      ) : (
                        batchInfo.batches.map((batch) => (
                          <TableRow key={batch.batchId}>
                            <TableCell className="font-mono text-xs">
                              {batch.batchId.substring(0, 16)}...
                            </TableCell>
                            <TableCell>{batch.messageCount}</TableCell>
                            <TableCell>
                              {batch.queues.map(queue => (
                                <Badge key={queue} variant="outline" className="mr-1 text-xs">
                                  {queue.replace('pl-', '')}
                                </Badge>
                              ))}
                            </TableCell>
                            <TableCell className="text-sm text-gray-600">
                              {formatTimeAgo(batch.firstSeen)} - {formatTimeAgo(batch.lastSeen)}
                            </TableCell>
                            <TableCell>
                              <Badge className={batch.status === 'active' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}>
                                {batch.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setMessageFilters(prev => ({ ...prev, batchId: batch.batchId }))}
                                >
                                  View
                                </Button>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button size="sm" variant="destructive">
                                      Purge
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Purge Batch</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        This will permanently delete all {batch.messageCount} messages from batch {batch.batchId}
                                        across all queues. This action cannot be undone.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => {
                                        // Purge from all queues this batch appears in
                                        batch.queues.forEach(queue => {
                                          handlePurgeQueue(queue, batch.batchId);
                                        });
                                      }}
                                      className="bg-red-600 hover:bg-red-700"
                                      disabled={!canExecute}
                                      title={!canExecute ? noExecuteMessage : undefined}
                                    >
                                      Purge All
                                    </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Message Detail Dialog */}
      <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Message Details</DialogTitle>
            <DialogDescription>
              Message ID: {selectedMessage?.id}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {selectedMessage && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Batch ID</Label>
                    <div className="font-mono text-sm bg-gray-100 p-2 rounded">
                      {selectedMessage.batchId || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <Label>Entity Name</Label>
                    <div className="font-mono text-sm bg-gray-100 p-2 rounded">
                      {selectedMessage.entityName || 'N/A'}
                    </div>
                  </div>
                </div>
                <div>
                  <Label>Message Body</Label>
                  <pre className="bg-gray-100 p-4 rounded text-xs overflow-auto max-h-96">
                    {JSON.stringify(selectedMessage.body, null, 2)}
                  </pre>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMessageDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </MonitoringLayout>
    </ViewGuard>
  );
}
